import { klinecharts } from "../klinecharts-client";

const OVERLAY_NAME = "vybeBidCountdown";
const LABEL_RIGHT_PADDING = 6;
const LABEL_COLOR = "#9ca3af";
const LABEL_BACKGROUND = "rgba(17, 24, 39, 0.6)";

interface BidCountdownExtendData {
  text: string;
}

function createPointFigures(params: any) {
  const { coordinates, bounding, overlay } = params;
  const data = overlay.extendData as BidCountdownExtendData | undefined;
  const point = coordinates?.[0];

  if (!data || !point || !data.text) {
    return [];
  }

  return [
    {
      key: "bidCountdownLabel",
      type: "text",
      attrs: {
        x: Math.max(0, (bounding?.width ?? 0) - LABEL_RIGHT_PADDING),
        y: point.y,
        text: data.text,
        align: "right",
        baseline: "middle",
      },
      styles: {
        style: "fill",
        color: LABEL_COLOR,
        size: 10,
        family: "Arial",
        weight: "normal",
        backgroundColor: LABEL_BACKGROUND,
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 2,
        paddingBottom: 2,
        borderRadius: 2,
      },
      ignoreEvent: true,
    },
  ];
}

export const bidCountdownOverlay = {
  name: OVERLAY_NAME,
  totalStep: 1,
  lock: true,
  visible: true,
  zLevel: 8,
  needDefaultPointFigure: false,
  needDefaultXAxisFigure: false,
  needDefaultYAxisFigure: false,
  mode: "normal" as const,
  createPointFigures,
};

let registered = false;

export function registerBidCountdownOverlay() {
  if (!klinecharts || registered) {
    return;
  }

  try {
    const supportedOverlays = klinecharts.getSupportOverlays?.();
    if (supportedOverlays?.includes?.(OVERLAY_NAME)) {
      registered = true;
      return;
    }

    klinecharts.registerOverlay(bidCountdownOverlay);
    registered = true;
  } catch (error) {
    console.error("Failed to register bid countdown overlay:", error);
  }
}

export function createBidCountdownOverlay(
  chartInstance: any,
  bid: number,
  text: string
): string | null {
  if (!chartInstance || !Number.isFinite(bid)) {
    return null;
  }

  try {
    const overlayId = chartInstance.createOverlay({
      name: OVERLAY_NAME,
      lock: true,
      points: [{ value: bid }],
      extendData: { text } as BidCountdownExtendData,
    });

    return (overlayId as string) ?? null;
  } catch (error) {
    console.error("Failed to create bid countdown overlay:", error);
    return null;
  }
}

export function updateBidCountdownOverlay(
  chartInstance: any,
  overlayId: string | null,
  bid: number,
  text: string
) {
  if (!chartInstance || !overlayId || !Number.isFinite(bid)) {
    return;
  }

  try {
    chartInstance.overrideOverlay({
      id: overlayId,
      points: [{ value: bid }],
      extendData: { text } as BidCountdownExtendData,
    });
  } catch (error) {
    // Overlay might have been removed externally; ignore.
  }
}

export function removeBidCountdownOverlay(
  chartInstance: any,
  overlayId: string | null
) {
  if (!chartInstance || !overlayId) {
    return;
  }

  try {
    chartInstance.removeOverlay(overlayId);
  } catch (error) {
    // Already removed; ignore.
  }
}

/**
 * Returns the timestamp (ms) when the current candle for `timeframe` is expected to close,
 * based on `now` (ms). Timeframe strings match `useChartStore3` lowercase values:
 * "1m","5m","15m","30m","1h","2h","4h","12h","1d","1w","1mn".
 */
export function getNextCandleCloseMs(now: number, timeframe: string): number {
  const date = new Date(now);

  switch (timeframe) {
    case "1m": {
      date.setSeconds(0, 0);
      return date.getTime() + 60 * 1000;
    }
    case "5m": {
      const minutes = Math.floor(date.getMinutes() / 5) * 5;
      date.setMinutes(minutes, 0, 0);
      return date.getTime() + 5 * 60 * 1000;
    }
    case "15m": {
      const minutes = Math.floor(date.getMinutes() / 15) * 15;
      date.setMinutes(minutes, 0, 0);
      return date.getTime() + 15 * 60 * 1000;
    }
    case "30m": {
      const minutes = Math.floor(date.getMinutes() / 30) * 30;
      date.setMinutes(minutes, 0, 0);
      return date.getTime() + 30 * 60 * 1000;
    }
    case "1h": {
      date.setMinutes(0, 0, 0);
      return date.getTime() + 60 * 60 * 1000;
    }
    case "2h": {
      const hours = Math.floor(date.getHours() / 2) * 2;
      date.setHours(hours, 0, 0, 0);
      return date.getTime() + 2 * 60 * 60 * 1000;
    }
    case "4h": {
      const hours = Math.floor(date.getHours() / 4) * 4;
      date.setHours(hours, 0, 0, 0);
      return date.getTime() + 4 * 60 * 60 * 1000;
    }
    case "12h": {
      const hours = Math.floor(date.getHours() / 12) * 12;
      date.setHours(hours, 0, 0, 0);
      return date.getTime() + 12 * 60 * 60 * 1000;
    }
    case "1d": {
      date.setHours(0, 0, 0, 0);
      return date.getTime() + 24 * 60 * 60 * 1000;
    }
    case "1w": {
      const d = date.getDay();
      const daysFromMonday = d === 0 ? 6 : d - 1;
      date.setDate(date.getDate() - daysFromMonday);
      date.setHours(0, 0, 0, 0);
      return date.getTime() + 7 * 24 * 60 * 60 * 1000;
    }
    case "1mn": {
      const year = date.getFullYear();
      const month = date.getMonth();
      return new Date(year, month + 1, 1, 0, 0, 0, 0).getTime();
    }
    default: {
      date.setSeconds(0, 0);
      return date.getTime() + 60 * 1000;
    }
  }
}

export function formatRemainingMs(ms: number): string {
  const clamped = Math.max(0, ms);
  const totalSeconds = Math.floor(clamped / 1000);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const totalHours = Math.floor(totalMinutes / 60);
  const hours = totalHours % 24;
  const days = Math.floor(totalHours / 24);

  const pad = (value: number) => value.toString().padStart(2, "0");

  if (days > 0) {
    return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  if (totalHours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  return `${pad(minutes)}:${pad(seconds)}`;
}
